import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Cartao, Selo } from '../../componentes/base';
import { Estado } from '../../componentes/estados';
import { get } from '../../nucleo/api';
import { data } from '../../nucleo/formato';

/**
 * crm8 item 1 (ficha 2026-10-29 "crm8"): o cartão "Verticais" da empresa e do contato no CRM, SÓ LEITURA. As verticais vêm
 * das negociações e dos contratos (`GET /cadastros/parceiros/:id/verticais-relacionamento`): cada uma Ativa (desde a data)
 * ou Encerrada (na data). Muda pela negociação ou pelo contrato; aqui não há o que editar.
 */
export interface VerticalRelacionamento {
  vertical_id: string | null;
  nome: string | null;
  todas: boolean;
  outra: boolean;
  situacao: 'ativa' | 'encerrada';
  desde: string;
  encerrada_em: string | null;
  periodos: { inicio_em: string; fim_em: string | null }[];
}

/** Os períodos anteriores ao que a linha mostra (o mais recente), do mais novo ao mais antigo; só os fechados. */
export function anteriores(v: Pick<VerticalRelacionamento, 'periodos'>) {
  const ordem = [...v.periodos].sort((a, b) => b.inicio_em.localeCompare(a.inicio_em));
  return ordem.slice(1).filter((p) => p.fim_em);
}

export const chaveVerticaisRelacionamento = (id: string) => ['crm', 'verticais-relacionamento', id];

export function CartaoVerticaisRelacionamento({ parceiroId }: { parceiroId: string }) {
  const { t } = useTranslation('cadastros');
  const q = useQuery({
    queryKey: chaveVerticaisRelacionamento(parceiroId),
    queryFn: () => get<{ itens: VerticalRelacionamento[] }>(`/cadastros/parceiros/${parceiroId}/verticais-relacionamento`),
  });
  return (
    <Cartao titulo={t('crm.relacao.titulo')}>
      <div className="flex flex-col gap-2" data-verticais-relacionamento="">
        <p className="text-xs text-tinta-3">{t('crm.relacao.ajuda')}</p>
        <Estado consulta={q}>
          {(r) =>
            r.itens.length ? (
              <ul className="flex flex-col">
                {r.itens.map((v, i) => (
                  <li key={`${v.vertical_id ?? (v.todas ? 'todas' : 'outra')}-${i}`} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 border-b border-borda px-2 py-2 text-sm last:border-0" data-vertical-situacao={v.situacao}>
                    <span className="min-w-0 font-medium break-words text-tinta-1">{v.todas ? t('crm.relacao.todas') : v.outra ? t('crm.relacao.outra') : v.nome}</span>
                    <span className="flex shrink-0 items-center gap-2 text-xs text-tinta-3">
                      <Selo cor={v.situacao === 'ativa' ? 'verde' : 'neutro'}>{t(`crm.relacao.${v.situacao}`)}</Selo>
                      {v.situacao === 'ativa' ? t('crm.relacao.desde', { data: data(v.desde) }) : t('crm.relacao.em', { data: data(v.encerrada_em) })}
                    </span>
                    {/* Rodada rlote-8 (SPEC-B1, crm8 item 1): a volta a uma vertical abre período novo; os anteriores ficam aqui embaixo. */}
                    {v.periodos.length > 1 && anteriores(v).length > 0 && (
                      <ul className="w-full basis-full text-xs text-tinta-3" aria-label={t('crm.relacao.antes')} data-periodos-anteriores="">
                        {anteriores(v).map((p) => (
                          <li key={p.inicio_em}>{t('crm.relacao.periodo', { de: data(p.inicio_em), ate: data(p.fim_em) })}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-tinta-3">{t('crm.relacao.vazio')}</p>
            )
          }
        </Estado>
      </div>
    </Cartao>
  );
}
